import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import './CapacityPartners.css';

/* ── helpers ── */
function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

function formatCompactCurrency(val) {
  if (val == null) return '—';
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val}`;
}

/* ── mock data ── */
const RISK_TIER_DATA = [
  { name: 'Low', value: 142, color: '#16a34a' },
  { name: 'Moderate-Low', value: 98, color: '#ca8a04' },
  { name: 'Moderate-High', value: 52, color: '#ea580c' },
  { name: 'High', value: 20, color: '#dc2626' },
];

const NEW_VS_RENEWAL_DATA = [
  { month: 'Aug', new: 38, renewal: 22 },
  { month: 'Sep', new: 45, renewal: 28 },
  { month: 'Oct', new: 42, renewal: 31 },
  { month: 'Nov', new: 51, renewal: 27 },
  { month: 'Dec', new: 48, renewal: 35 },
  { month: 'Jan', new: 56, renewal: 33 },
];

const INDUSTRY_DATA = [
  { name: 'Technology', value: 28, color: '#2563eb' },
  { name: 'Healthcare', value: 18, color: '#7c3aed' },
  { name: 'Financial Services', value: 15, color: '#0891b2' },
  { name: 'Manufacturing', value: 12, color: '#16a34a' },
  { name: 'Professional Services', value: 10, color: '#ca8a04' },
  { name: 'Retail', value: 8, color: '#ea580c' },
  { name: 'Other', value: 9, color: '#94a3b8' },
];

const PREMIUM_TREND_DATA = [
  { month: 'Feb', premium: 2_050_000 },
  { month: 'Mar', premium: 2_180_000 },
  { month: 'Apr', premium: 2_350_000 },
  { month: 'May', premium: 2_420_000 },
  { month: 'Jun', premium: 2_710_000 },
  { month: 'Jul', premium: 2_950_000 },
  { month: 'Aug', premium: 3_100_000 },
  { month: 'Sep', premium: 3_280_000 },
  { month: 'Oct', premium: 3_520_000 },
  { month: 'Nov', premium: 3_810_000 },
  { month: 'Dec', premium: 4_150_000 },
  { month: 'Jan', premium: 4_480_000 },
];

const CONVERSION_TREND_DATA = [
  { month: 'Feb', rate: 29.5 },
  { month: 'Mar', rate: 30.2 },
  { month: 'Apr', rate: 31.1 },
  { month: 'May', rate: 30.8 },
  { month: 'Jun', rate: 32.4 },
  { month: 'Jul', rate: 33.1 },
  { month: 'Aug', rate: 33.9 },
  { month: 'Sep', rate: 34.6 },
  { month: 'Oct', rate: 35.8 },
  { month: 'Nov', rate: 36.2 },
  { month: 'Dec', rate: 37.1 },
  { month: 'Jan', rate: 37.9 },
];

const GEO_DATA = [
  { state: 'CA', premium: 4_200_000 },
  { state: 'NY', premium: 3_800_000 },
  { state: 'TX', premium: 3_100_000 },
  { state: 'FL', premium: 2_700_000 },
  { state: 'IL', premium: 2_100_000 },
  { state: 'PA', premium: 1_900_000 },
  { state: 'OH', premium: 1_600_000 },
  { state: 'GA', premium: 1_400_000 },
  { state: 'NJ', premium: 1_200_000 },
  { state: 'MA', premium: 1_100_000 },
];

const BROKERS_DATA = [
  { rank: 1, name: 'CRC Group - Atlanta', type: 'wholesale', submissions: 124, bound: 52, premium: 3_850_000, conversion: 41.9 },
  { rank: 2, name: 'Amwins - Charlotte', type: 'wholesale', submissions: 118, bound: 47, premium: 3_420_000, conversion: 39.8 },
  { rank: 3, name: 'RT Specialty - Chicago', type: 'wholesale', submissions: 96, bound: 38, premium: 2_980_000, conversion: 39.6 },
  { rank: 4, name: 'Risk Placement Services - Rolling Meadows', type: 'wholesale', submissions: 87, bound: 33, premium: 2_540_000, conversion: 37.9 },
  { rank: 5, name: 'Burns & Wilcox - Detroit', type: 'wholesale', submissions: 78, bound: 28, premium: 2_180_000, conversion: 35.9 },
  { rank: 6, name: 'USI Insurance - Valhalla', type: 'retail', submissions: 72, bound: 27, premium: 1_920_000, conversion: 37.5 },
  { rank: 7, name: 'Hub International - Chicago', type: 'retail', submissions: 65, bound: 24, premium: 1_760_000, conversion: 36.9 },
  { rank: 8, name: 'Lockton Companies - Kansas City', type: 'retail', submissions: 61, bound: 22, premium: 1_580_000, conversion: 36.1 },
  { rank: 9, name: 'Brown & Brown - Daytona Beach', type: 'retail', submissions: 54, bound: 19, premium: 1_340_000, conversion: 35.2 },
  { rank: 10, name: 'Marsh McLennan Agency - New York', type: 'retail', submissions: 48, bound: 17, premium: 1_150_000, conversion: 35.4 },
];

/* ── custom tooltip components ── */
function CustomBarTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="cp__tooltip">
      <div className="cp__tooltip-label">{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="cp__tooltip-row">
          <span>{entry.name}</span>
          <span style={{ color: entry.color, fontWeight: 600 }}>{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

function CustomLineTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="cp__tooltip">
      <div className="cp__tooltip-label">{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="cp__tooltip-row">
          <span>{entry.name}</span>
          <span style={{ color: entry.color, fontWeight: 600 }}>
            {entry.dataKey === 'premium'
              ? formatCompactCurrency(entry.value)
              : `${entry.value}%`}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── geo color intensity ── */
function getGeoStyles(premium) {
  const maxPremium = 4_200_000;
  const ratio = premium / maxPremium;
  if (ratio >= 0.8) return { background: '#1e40af', color: '#fff' };
  if (ratio >= 0.6) return { background: '#2563eb', color: '#fff' };
  if (ratio >= 0.4) return { background: '#60a5fa', color: '#1e3a5f' };
  if (ratio >= 0.25) return { background: '#93c5fd', color: '#1e3a5f' };
  return { background: '#dbeafe', color: '#1e40af' };
}

/* ── main component ── */
export function CapacityPartners() {
  return (
    <div className="cp">
      {/* Header */}
      <div className="cp__header">
        <h1 className="cp__title">Capacity Partners</h1>
        <p className="cp__subtitle">Dashboard Template</p>
        <div className="cp__info-banner">
          <span className="cp__info-icon">ℹ️</span>
          <span>
            This dashboard uses sample data for demonstration purposes. Each
            capacity partner's view will be customized to their preferences.
          </span>
        </div>
      </div>

      {/* 1. KPI Stat Cards */}
      <div className="cp__stats">
        <div className="cp__stat-card card">
          <span className="cp__stat-label">Submission Volume</span>
          <span className="cp__stat-value">847</span>
          <span className="cp__stat-detail">New submissions</span>
        </div>
        <div className="cp__stat-card card">
          <span className="cp__stat-label">Bound Policies</span>
          <span className="cp__stat-value">312</span>
          <span className="cp__stat-detail">Active policies</span>
        </div>
        <div className="cp__stat-card card">
          <span className="cp__stat-label">Avg Premium</span>
          <span className="cp__stat-value">$28,450</span>
          <span className="cp__stat-detail">Per policy</span>
        </div>
        <div className="cp__stat-card card">
          <span className="cp__stat-label">Quote-to-Bind</span>
          <span className="cp__stat-value">36.8%</span>
          <span className="cp__stat-detail">Conversion rate</span>
        </div>
      </div>

      {/* 2. Risk Tier + 4. Loss Ratio — side by side */}
      <div className="cp__chart-row">
        <div className="cp__section">
          <h2 className="cp__section-title">Risk Tier Breakdown</h2>
          <div className="cp__chart-card card">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={RISK_TIER_DATA}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={110}
                  paddingAngle={3}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {RISK_TIER_DATA.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="cp__section">
          <h2 className="cp__section-title">Loss Ratio</h2>
          <div className="cp__loss-ratio card">
            <span className="cp__stat-label">Current Loss Ratio</span>
            <span className="cp__loss-ratio-value">42.3%</span>
            <span className="cp__loss-ratio-trend">
              ↓ 3.2% vs prior year
            </span>
            <span className="cp__loss-ratio-label">
              Below 50% target — performing well
            </span>
          </div>
        </div>
      </div>

      {/* 3. New vs Renewal Policies */}
      <div className="cp__section">
        <h2 className="cp__section-title">New vs Renewal Policies</h2>
        <div className="cp__chart-card--full card">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={NEW_VS_RENEWAL_DATA}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" tick={{ fontSize: 13 }} />
              <YAxis tick={{ fontSize: 13 }} />
              <Tooltip content={<CustomBarTooltip />} />
              <Legend />
              <Bar
                dataKey="new"
                name="New Business"
                stackId="a"
                fill="#2563eb"
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="renewal"
                name="Renewals"
                stackId="a"
                fill="#60a5fa"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 5. Industry + 7. Quote-to-Bind Conversion — side by side */}
      <div className="cp__chart-row">
        <div className="cp__section">
          <h2 className="cp__section-title">Industry Breakdown</h2>
          <div className="cp__chart-card card">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={INDUSTRY_DATA}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, value }) => `${name} ${value}%`}
                >
                  {INDUSTRY_DATA.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value}%`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="cp__section">
          <h2 className="cp__section-title">Quote-to-Bind Conversion Rate</h2>
          <div className="cp__chart-card card">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={CONVERSION_TREND_DATA}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="month" tick={{ fontSize: 13 }} />
                <YAxis
                  domain={[25, 42]}
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fontSize: 13 }}
                />
                <Tooltip content={<CustomLineTooltip />} />
                <Area
                  type="monotone"
                  dataKey="rate"
                  name="Conversion Rate"
                  stroke="#2563eb"
                  fill="#dbeafe"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* 6. Premium Volume Trends */}
      <div className="cp__section">
        <h2 className="cp__section-title">Premium Volume Trends</h2>
        <div className="cp__chart-card--full card">
          <ResponsiveContainer width="100%" height={340}>
            <LineChart data={PREMIUM_TREND_DATA}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" tick={{ fontSize: 13 }} />
              <YAxis
                tickFormatter={(v) => formatCompactCurrency(v)}
                tick={{ fontSize: 13 }}
              />
              <Tooltip content={<CustomLineTooltip />} />
              <Line
                type="monotone"
                dataKey="premium"
                name="Written Premium"
                stroke="#2563eb"
                strokeWidth={2.5}
                dot={{ r: 4, fill: '#2563eb' }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 8. Geographic Distribution */}
      <div className="cp__section">
        <h2 className="cp__section-title">Geographic Distribution</h2>
        <div className="cp__chart-card--full card">
          <div className="cp__geo-grid">
            {GEO_DATA.map((item) => {
              const styles = getGeoStyles(item.premium);
              return (
                <div
                  key={item.state}
                  className="cp__geo-card"
                  style={styles}
                >
                  <span className="cp__geo-state">{item.state}</span>
                  <span className="cp__geo-premium">
                    {formatCompactCurrency(item.premium)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 9. Top Producers / Brokers */}
      <div className="cp__section">
        <h2 className="cp__section-title">Top Producers / Brokers</h2>
        <div className="cp__table-wrap card">
          <table className="cp__table">
            <thead>
              <tr>
                <th>#</th>
                <th>Broker Name</th>
                <th>Type</th>
                <th>Submissions</th>
                <th>Bound</th>
                <th>Premium</th>
                <th>Conversion</th>
              </tr>
            </thead>
            <tbody>
              {BROKERS_DATA.map((broker) => (
                <tr key={broker.rank}>
                  <td className="cp__table-rank">{broker.rank}</td>
                  <td className="cp__table-broker">{broker.name}</td>
                  <td>
                    <span
                      className={`cp__table-type cp__table-type--${broker.type}`}
                    >
                      {broker.type}
                    </span>
                  </td>
                  <td>{broker.submissions}</td>
                  <td>{broker.bound}</td>
                  <td>{formatCurrency(broker.premium)}</td>
                  <td>
                    <span
                      className={`cp__table-conversion ${
                        broker.conversion >= 38
                          ? 'cp__table-conversion--high'
                          : broker.conversion >= 36
                            ? 'cp__table-conversion--mid'
                            : 'cp__table-conversion--low'
                      }`}
                    >
                      {broker.conversion}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
